import {
  STAGES,
  STAGES_UP_TO_CARE,
  STAGES_WITH_ARRANGEMENT_ACCESS,
  type ArrangementStatus,
  type Stage,
} from "constants/at-need";

const TERMINAL_ARRANGEMENT_STATUSES: readonly ArrangementStatus[] = [
  "submitted",
  "completed",
];

// Type for Stage Details object
export interface StageDisplayDetails {
  name: string;
  description: string;
}

export function isArrangementAccessibleStage(
  stage: string | null | undefined,
): boolean {
  return (
    !!stage &&
    (STAGES_WITH_ARRANGEMENT_ACCESS as readonly string[]).includes(stage)
  );
}

export function isArrangementLocked(
  status: string | null | undefined,
): boolean {
  return (
    !!status &&
    (TERMINAL_ARRANGEMENT_STATUSES as readonly string[]).includes(status)
  );
}

/**
 * Ordinal stage comparison: true when `stage` is `target` or any later
 * stage in the canonical STAGES progression. Missing or unknown stages
 * compare as not reached.
 */
export function hasReachedStage(
  stage: string | null | undefined,
  target: Stage,
): boolean {
  if (!stage) return false;
  return (STAGES as readonly string[]).indexOf(stage) >= STAGES.indexOf(target);
}

/**
 * A case only reaches the Docs stage after its consultation has
 * happened, so at Docs or beyond the family no longer needs to
 * schedule one.
 */
export function isConsultationCompleteForStage(
  stage: string | null | undefined,
): boolean {
  return hasReachedStage(stage, "Docs");
}

/**
 * Reads `properties.arrangement.status` from a `/cases/:id/app-data`
 * response. Returns null when the field is missing or the response shape
 * doesn't include it.
 */
export function getArrangementStatus(appData: unknown): string | null {
  const arrangement = (
    appData as
      { properties?: { arrangement?: { status?: string | null } } } | undefined
  )?.properties?.arrangement;
  return arrangement?.status ?? null;
}

/**
 * Single source of truth for whether a case contact may reach the
 * arrangement flow. The timeline "Complete Arrangement" CTA and the
 * arrangement route guard both derive their decision from this, so they
 * can never disagree and trap the family in a redirect loop.
 */
export function canAccessArrangementFlow(
  stage: string | null | undefined,
  arrangementStatus: string | null | undefined,
): boolean {
  // Submitted/completed arrangements still open the flow (read-only
  // confirmation); the timeline marks the step done independently.
  if (isArrangementLocked(arrangementStatus)) {
    return true;
  }
  // Otherwise the family can reach the flow while the case is in an
  // arrangement-access stage (Dispatch/Care/Docs), or whenever a draft
  // exists to finish even if the case advanced past those stages.
  return isArrangementAccessibleStage(stage) || arrangementStatus != null;
}

export function isPastArrangementWindow(
  stage: string | null | undefined,
): boolean {
  return !!stage && !(STAGES_UP_TO_CARE as readonly string[]).includes(stage);
}

/**
 * Provides user-friendly names and descriptions for internal case stage slugs.
 * @param stage The internal stage slug (e.g., "Vitals", "DC Ordered").
 * @returns An object containing the display name and description for the stage.
 */
export const getStageDisplayDetails = (stage?: string): StageDisplayDetails => {
  switch (stage) {
    case "Lead":
    case "Case Secured":
      return {
        name: "Getting Started",
        description:
          "We are gathering initial information and preparing for the next steps in the arrangement process.",
      };
    case "Dispatch":
      return {
        name: "Bringing Into Our Care",
        description:
          "Our specialized care team has been dispatched to bring your loved one into our dedicated facility.",
      };
    case "Care":
      return {
        name: "In Our Care",
        description:
          "Your loved one is now resting in our care. We are ensuring they are treated with dignity and respect while we coordinate the necessary arrangements.",
      };
    case "Docs":
      return {
        name: "Preparing Documents",
        description:
          "We are preparing and sending the necessary documents, like the Cremation Authorization Form, for your review and signature. This helps us proceed with obtaining the required permits.",
      };
    case "Vitals & Permit":
      return {
        name: "Securing Permits",
        description:
          "We are working diligently with medical offices and government agencies to obtain the necessary clearances and secure the required cremation permits.",
      };
    case "Fulfillment":
      return {
        name: "Cremation Pending",
        description:
          "The required permits have been approved, and we are coordinating with the crematory for the cremation process.",
      };
    case "DC Ordered":
      return {
        name: "Ordering Requested Documents",
        description:
          "We are processing any requests for official documents, such as death certificates, according to the arrangements made.",
      };
    case "Cremation":
      return {
        name: "Cremation Scheduled",
        description:
          "The cremation is being scheduled with the crematory based on their timeline. We will notify you upon completion.",
      };
    case "Delivery":
      return {
        name: "Arranging Final Delivery",
        description:
          "We are coordinating the final delivery or pickup of your loved one's remains and any requested items. We'll contact you soon to confirm details.",
      };
    case "Complete":
      return {
        name: "Arrangement Complete",
        description:
          "All steps of the arrangement process are now complete. We thank you for trusting us during this difficult time.",
      };
    default:
      return {
        name: stage || "Processing",
        description:
          "The current stage information is being updated. Please check back shortly.",
      };
  }
};
